import React from 'react';
import { ArrowRight, TrendingUp, Zap, Building2 } from 'lucide-react';

const HOTEL_IMG = 'https://images.unsplash.com/photo-1723465308831-29da05e011f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzg0MDY3MDcwfDA&ixlib=rb-4.1.0&q=85';

/**
 * V2D — The Vanguard Hybrid
 * Editorial serif + real hotel photography + frosted-glass UI overlays.
 * Blends Image 1's product proof, Image 2's warmth, Image 3's editorial authority.
 */
export default function LandingV2D({ onGoToLogin, onGoToSignup }) {
  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'DM Sans, system-ui, sans-serif', color: '#0F172A' }}>
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-6 pt-6 flex items-center justify-between" data-testid="v2d-nav">
        <div className="flex items-center gap-2.5">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
            <path d="M15 3C15 3 6 13 6 20a9 9 0 0018 0c0-7-9-17-9-17z" fill="#6D28D9" />
          </svg>
          <span className="text-xl tracking-tight" style={{ fontFamily: 'Playfair Display', fontWeight: 700 }}>LodgiFlow</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-gray-700">
          <a href="#platform" className="hover:text-gray-900">Platform</a>
          <a href="#pricing" className="hover:text-gray-900">Pricing</a>
          <a href="#customers" className="hover:text-gray-900">Customers</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onGoToLogin} className="text-sm text-gray-700 hover:text-gray-900" data-testid="v2d-login">Log In</button>
          <button onClick={onGoToSignup} className="group inline-flex items-center gap-1.5 px-5 py-2.5 rounded-2xl text-sm font-semibold text-white transition-transform hover:scale-[1.03]" style={{ background: '#6D28D9' }} data-testid="v2d-cta-nav">
            Request Demo
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </nav>

      {/* Hero — bento asymmetric */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-[1.05fr_1.2fr] gap-10 items-start">
        {/* Left — editorial copy */}
        <div className="pt-8">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="h-px w-8 bg-purple-600" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-purple-700">Enterprise hospitality tech</span>
          </div>
          <h1 style={{ fontFamily: 'Playfair Display', fontWeight: 600, fontSize: 'clamp(2.5rem, 6vw, 4.75rem)', lineHeight: 1.03, letterSpacing: '-0.025em' }} data-testid="v2d-headline">
            Master your hospitality tech,{' '}
            <em style={{ color: '#6D28D9', fontStyle: 'italic', fontWeight: 500 }}>beautifully.</em>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-lg">
            Scale from 5 to 500 properties with absolute clarity. Stop wrangling spreadsheets and start leading with real-time governance.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button onClick={onGoToSignup} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl text-sm font-bold text-white transition-transform hover:scale-[1.03]" style={{ background: '#6D28D9', boxShadow: '0 12px 30px -10px rgba(109,40,217,0.4)' }} data-testid="v2d-cta-primary">
              Start Free Trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button onClick={onGoToLogin} className="px-6 py-3.5 rounded-2xl text-sm font-semibold border border-gray-300 hover:border-gray-500 hover:bg-white transition-colors" data-testid="v2d-cta-secondary">
              Request Demo
            </button>
          </div>

          {/* Inline logo cloud */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 mb-3">In good company</div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 opacity-70">
              {['OMNI', 'LOEWS', 'HIGHGATE', 'KIMPTON', 'SONESTA'].map((b) => (
                <span key={b} style={{ fontFamily: 'Playfair Display', fontWeight: 600, letterSpacing: '0.15em', fontSize: '13px' }}>{b}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Right — bento visual grid */}
        <div className="relative grid grid-cols-2 grid-rows-[auto_auto] gap-4">
          {/* Big hotel photo — spans 2 rows */}
          <div className="relative col-span-2 row-span-1 rounded-3xl overflow-hidden aspect-[16/9]" style={{ boxShadow: '0 30px 60px -20px rgba(15,23,42,0.25)' }}>
            <img src={HOTEL_IMG} alt="Luxury hotel exterior" className="w-full h-full object-cover animate-[slowZoom_20s_ease-in-out_infinite_alternate]" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.35) 0%, rgba(109,40,217,0.15) 100%)' }} />
            {/* Overlay glass badge */}
            <div className="absolute top-4 left-4 rounded-full px-3 py-1.5 flex items-center gap-1.5 backdrop-blur-xl border border-white/40" style={{ background: 'rgba(255,255,255,0.25)' }}>
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-semibold text-white uppercase tracking-wider">Live · 47 properties</span>
            </div>
            {/* Overlay glass card — bottom right */}
            <div className="absolute bottom-4 right-4 rounded-2xl p-4 backdrop-blur-xl border border-white/30 w-56" style={{ background: 'rgba(255,255,255,0.85)' }}>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-purple-100">
                  <TrendingUp className="h-4 w-4 text-purple-700" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Portfolio spend</div>
              </div>
              <div className="mt-2 text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>$24.8M</div>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                <TrendingUp className="h-3 w-3" /> 15% optimized this quarter
              </div>
            </div>
          </div>

          {/* Stat card 1 */}
          <div className="rounded-3xl p-6 bg-white border border-gray-100 transition-all hover:-translate-y-1" style={{ boxShadow: '0 4px 16px -6px rgba(15,23,42,0.1)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-purple-50">
                <Zap className="h-4 w-4 text-purple-700" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">+12 this month</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>900+</div>
            <div className="text-xs text-gray-500 mt-1">Products & vendors catalogued across the industry</div>
          </div>

          {/* Stat card 2 */}
          <div className="rounded-3xl p-6 border transition-all hover:-translate-y-1" style={{ background: 'linear-gradient(135deg, #4C1D95, #6D28D9)', color: 'white', borderColor: 'transparent', boxShadow: '0 12px 30px -10px rgba(76,29,149,0.4)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-white/15 backdrop-blur">
                <Building2 className="h-4 w-4 text-white" />
              </div>
              <span className="text-[10px] uppercase tracking-wider text-purple-200 font-semibold">Multi-tenant</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>500</div>
            <div className="text-xs text-purple-100 mt-1">Properties on a single, unified control plane</div>
          </div>
        </div>
      </section>

      {/* Interactive proof grid fold */}
      <section className="border-t border-gray-200" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto px-6 py-20" data-testid="v2d-proof">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 — trust */}
            <div className="rounded-3xl p-8 border border-gray-100 transition-all hover:-translate-y-1" style={{ background: 'linear-gradient(145deg, #FAFAFA, #FFFFFF)', boxShadow: '0 4px 20px -8px rgba(0,0,0,0.06)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 mb-4">Enterprise-grade</div>
              <div style={{ fontFamily: 'Playfair Display', fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.15 }}>SOC 2 · GDPR · WCAG 2.2 AA</div>
              <div className="mt-4 text-sm text-gray-600 leading-relaxed">12 compliance frameworks tracked per property, per platform, with evidence links and expiry alerts.</div>
            </div>

            {/* Card 2 — dashboard peek */}
            <div className="rounded-3xl p-8 border border-purple-100 transition-all hover:-translate-y-1" style={{ background: 'linear-gradient(145deg, #F5F3FF, #EDE9FE)', boxShadow: '0 4px 20px -8px rgba(109,40,217,0.15)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-700 mb-4">Real-time governance</div>
              <div className="flex items-baseline gap-2">
                <span style={{ fontFamily: 'Playfair Display', fontSize: '3rem', fontWeight: 700, color: '#4C1D95', lineHeight: 1 }}>94%</span>
                <span className="text-sm text-purple-800 font-semibold">brand compliance</span>
              </div>
              <div className="mt-3 space-y-1.5">
                {[['PMS', 96], ['Payment', 100], ['Wi-Fi', 88]].map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11px]">
                    <span className="w-16 text-purple-900 font-medium">{k}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-purple-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${v}%`, background: '#6D28D9' }} />
                    </div>
                    <span className="text-purple-700 font-semibold tabular-nums">{v}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Card 3 — human quote */}
            <div className="rounded-3xl p-8 border border-gray-100 transition-all hover:-translate-y-1" style={{ background: '#0F172A', color: 'white', boxShadow: '0 12px 30px -12px rgba(15,23,42,0.4)' }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-300 mb-4">The team's take</div>
              <div style={{ fontFamily: 'Playfair Display', fontSize: '1.35rem', fontWeight: 500, lineHeight: 1.3, fontStyle: 'italic' }}>
                &ldquo;It's the difference between managing tech and leading it.&rdquo;
              </div>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-purple-600 flex items-center justify-center text-xs font-bold">JT</div>
                <div>
                  <div className="text-sm font-semibold">Jonathan Therron</div>
                  <div className="text-[11px] text-white/60">CIO, Innisfree Hospitality</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes slowZoom { from { transform: scale(1); } to { transform: scale(1.06); } }
      `}</style>
    </div>
  );
}
